import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPlanningDayLocks,
  patchScopeAspects,
  updateScope,
} from "../../../../../services/api/planning";
import { postScheduleBlockApplyWorkers } from "../../../../../services/api/schedule";
import {
  getShiftedDate,
  getWeekStart,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../../utils/datetime/core";
import {
  mapAspectFromApi,
  mapBlockFromApi,
} from "../../model/scheduleApiMappers";
import { getAspectBlockId } from "../../model/scheduleBlockSelectors";
import {
  dateRangeHasLockedDay,
  overrideRangeResizeChangedDaysTouchLockedDay,
} from "../../model/scheduleLockGuards";
import { sameScheduleBlockId } from "../../model/scheduleIds";
import { PLANNING_OPERATION_MESSAGES } from "../../../../../config";
import {
  buildMapByNumericId,
  pickItemsByNumericIdsInOrder,
} from "../../../../../utils/collections/numericId";
import {
  buildFilteredWorkerIdsAfterConflictStrip,
  getWorkerConflictSummariesByDate,
} from "../../../shared/planningWorkerConflicts";

/**
 * Crew assignment interactions: block/day modal edits, override strips, and clipboard copy/paste.
 *
 * @param {Object} params
 * @param {Array<Object>} params.blocks
 * @param {import('react').MutableRefObject<Array<Object>>} params.blocksRef
 * @param {Array<Object>} params.workScopeAspects
 * @param {import('react').MutableRefObject<Array<Object>>} params.workScopeAspectsRef
 * @param {Array<Object>|undefined} params.workers Crew catalog.
 * @param {import('react').MutableRefObject<Array<Object>>} params.blockDraftWorkers Draft crew during create.
 * @param {Function} params.setBlocks
 * @param {Function} params.setWorkScopeAspects
 * @param {Function} params.runPlanningOperation
 * @param {Record<string, boolean>} [params.scheduleDayLocks]
 * @param {(opts: { conflictSummaries: Array<Object>, onConfirm: () => void }) => void} [params.requestWorkerConflictConfirmation]
 * @param {(message: string, type?: string) => void} [params.notify]
 * @returns {Object} Crew modal state, conflict modals, and bar/override handlers (see hook return).
 */
export function useScheduleCrewActions({
  blocks,
  blocksRef,
  workScopeAspects,
  workScopeAspectsRef,
  workers,
  blockDraftWorkers,
  setBlocks,
  setWorkScopeAspects,
  runPlanningOperation,
  scheduleDayLocks = {},
  requestWorkerConflictConfirmation,
  notify,
}) {
  const [aspectWorkersModal, setAspectWorkersModal] = useState(null);
  const [scheduleCrewClipboard, setScheduleCrewClipboard] = useState(null);
  const [workerUnskipConflictModal, setWorkerUnskipConflictModal] = useState(null);
  const [workerModalDayDetails, setWorkerModalDayDetails] = useState([]);

  /** Load Dispatch day details for the open crew modal week so off-work workers are disabled. */
  useEffect(() => {
    const modalDay = aspectWorkersModal?.day;
    if (!(modalDay instanceof Date)) {
      setWorkerModalDayDetails([]);
      return undefined;
    }
    let cancelled = false;
    const ws = getWeekStart(modalDay);
    const rangeStart = toLocalDateKey(ws);
    const rangeEnd = toLocalDateKey(getShiftedDate(ws, 6));
    (async () => {
      try {
        const details = await getPlanningDayLocks(rangeStart, rangeEnd);
        if (!cancelled) {
          setWorkerModalDayDetails(Array.isArray(details) ? details : []);
        }
      } catch (error) {
        console.error("Schedule worker modal day details load failed", error);
        if (!cancelled) {
          setWorkerModalDayDetails([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aspectWorkersModal?.day]);

  /** Same-day Schedule assignments plus Dispatch off-work workers for WorkerSelectModal. */
  const scheduleWorkerModalUsedWorkers = useMemo(() => {
    const modalDay = aspectWorkersModal?.day;
    if (!(modalDay instanceof Date)) {
      return [];
    }
    if (aspectWorkersModal?.crewEditMode === "block") {
      // Block default-crew edits should allow selecting any worker; conflicts are
      // evaluated only after submit and resolved via per-day overrides.
      return [];
    }
    const dayKey = toLocalDateKey(modalDay);
    const editedAspectId = String(aspectWorkersModal?.aspectId ?? "");
    const byId = new Map();
    for (const aspect of workScopeAspects ?? []) {
      if (
        String(aspect.id) === editedAspectId ||
        aspect.skipped ||
        toLocalDateKey(aspect.date) !== dayKey
      ) {
        continue;
      }
      for (const worker of aspect.worker_assignment ?? []) {
        if (worker?.id != null) {
          byId.set(Number(worker.id), worker);
        }
      }
    }
    const dayDetails = workerModalDayDetails.find((day) => day.date === dayKey);
    for (const worker of dayDetails?.off_work ?? []) {
      if (worker?.id != null) {
        byId.set(Number(worker.id), worker);
      }
    }
    return [...byId.values()];
  }, [
    aspectWorkersModal?.aspectId,
    aspectWorkersModal?.crewEditMode,
    aspectWorkersModal?.day,
    workScopeAspects,
    workerModalDayDetails,
  ]);

  /** Open per-day crew editor from a short click on a bar day chip. */
  const handleBarDayShortClick = useCallback(
    (blockId, day) => {
      const dayKey = toLocalDateKey(day);
      const aspect = workScopeAspects.find(
        (a) =>
          sameScheduleBlockId(getAspectBlockId(a), blockId) &&
          toLocalDateKey(a.date) === dayKey
      );
      if (!aspect || !/^\d+$/.test(String(aspect.id))) {
        return;
      }
      const block = blocks.find((b) => sameScheduleBlockId(b.id, blockId));
      if (!block) {
        return;
      }
      setAspectWorkersModal({
        crewEditMode: "perDay",
        aspectId: aspect.id,
        day: new Date(day),
        block,
        scope: block.scope,
        job: block.job,
        selectedWorkerIds: (aspect.worker_assignment ?? []).map((w) => w.id),
      });
    },
    [blocks, workScopeAspects]
  );

  /**
   * Block-level default crew (not per-day aspect row); submit uses apply-workers API only.
   */
  const handleOpenScopeCrewModal = useCallback((block, anchorDate, options) => {
    if (!block?.id) {
      setAspectWorkersModal({
        crewEditMode: "block",
        aspectId: "block-draft",
        selectedWorkerIds: blockDraftWorkers.current,
      });
      return;
    }
    let day;
    if (anchorDate instanceof Date) {
      day = toLocalCalendarDay(anchorDate);
    } else if (block.startDate != null) {
      day = toLocalCalendarDay(block.startDate);
    } else {
      return;
    }
    setAspectWorkersModal({
      crewEditMode: "block",
      aspectId: `block-${block.id}`,
      day,
      block,
      selectedWorkerIds: (block.worker_assignment ?? []).map((w) => w.id),
      fromScopeSettings: Boolean(options?.fromScopeSettings),
    });
  }, [blockDraftWorkers]);

  /** Close crew assignment modal for block/day editing flows. */
  const closeAspectWorkersModal = useCallback(() => {
    setAspectWorkersModal(null);
  }, []);

  /** Apply block default crew to all block days via schedule API and refresh local rows. */
  const applyAspectWorkersToAllDays = useCallback(
    async (workerIds, context = {}) => {
      const modal = aspectWorkersModal;
      const targetBlockFromContext = context.block ?? null;
      const modalBlock = modal?.block ?? null;
      const sourceBlock = targetBlockFromContext ?? modalBlock;
      if (!sourceBlock?.id) {
        return;
      }
      const blockId = sourceBlock.id;
      const targetBlock =
        blocksRef.current.find((b) => sameScheduleBlockId(b.id, blockId)) ??
        sourceBlock;
      if (!targetBlock) {
        return;
      }
      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);
      const hydrate = (ids) => pickItemsByNumericIdsInOrder(byId, ids);
      const oldDefaultIdsSorted = (targetBlock.worker_assignment ?? [])
        .map((worker) => Number(worker?.id))
        .filter((id) => Number.isFinite(id))
        .sort((a, b) => a - b);
      const changedDateKeys = [];
      const sourceAspects = context.aspects ?? workScopeAspectsRef.current;
      const mergedAspectsForCheck = sourceAspects.map((row) => {
        if (!sameScheduleBlockId(getAspectBlockId(row), blockId)) {
          return row;
        }
        const rowIdsSorted = (row.worker_assignment ?? [])
          .map((worker) => Number(worker?.id))
          .filter((id) => Number.isFinite(id))
          .sort((a, b) => a - b);
        const rowMatchesOldDefault =
          rowIdsSorted.length === oldDefaultIdsSorted.length &&
          rowIdsSorted.every((id, index) => id === oldDefaultIdsSorted[index]);
        if (!rowMatchesOldDefault) {
          return row;
        }
        changedDateKeys.push(toLocalDateKey(row.date));
        return { ...row, worker_assignment: hydrate(workerIds) };
      });
      const conflictSummaries = getWorkerConflictSummariesByDate(
        mergedAspectsForCheck,
        [...new Set(changedDateKeys)],
        { workers }
      ).filter((summary) => summary.conflictWorkerIds.length > 0);

      const runApplyWorkers = async (summariesForResolution = []) => {
        const raw = await runPlanningOperation({
          module: "schedule",
          operation: "updateScheduleBlockDefaultCrew",
          resources: ["schedule.blocks", "schedule.aspects"],
          apiCall: () => postScheduleBlockApplyWorkers(blockId, workerIds),
        });
        const mapped = mapBlockFromApi(raw);
        const mappedAspects = (raw.aspects ?? []).map((a) =>
          mapAspectFromApi(a, mapped.id)
        );
        setBlocks((prev) =>
          prev.map((b) => (sameScheduleBlockId(b.id, blockId) ? mapped : b))
        );
        setWorkScopeAspects((prev) => [
          ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
          ...mappedAspects,
        ]);

        if (summariesForResolution.length > 0) {
          const summaryByDateKey = new Map(
            summariesForResolution.map((summary) => [summary.dateKey, summary])
          );
          const conflictPatchRows = [];
          for (const aspect of mappedAspects) {
            if (!/^\d+$/.test(String(aspect.id))) {
              continue;
            }
            const summary = summaryByDateKey.get(toLocalDateKey(aspect.date));
            if (!summary || summary.conflictWorkerIds.length === 0) {
              continue;
            }
            const filteredWorkerIds = buildFilteredWorkerIdsAfterConflictStrip({
              block: mapped,
              aspect,
              conflictWorkerIds: summary.conflictWorkerIds,
            });
            conflictPatchRows.push({
              scopeId: Number(aspect.id),
              workerIds: filteredWorkerIds,
              idKey: String(aspect.id),
            });
          }
          if (conflictPatchRows.length > 0) {
            await runPlanningOperation({
              module: "schedule",
              operation: "updateScheduleConflictCrewOverrides",
              resources: ["schedule.aspects"],
              apiCall: () =>
                patchScopeAspects(
                  conflictPatchRows.map(({ scopeId, workerIds: rowWorkerIds }) => ({
                    scopeId,
                    workerIds: rowWorkerIds,
                  })),
                  undefined,
                  "schedule"
                ),
            });
            const nextAssignmentsByAspect = new Map(
              conflictPatchRows.map(({ idKey, workerIds: rowWorkerIds }) => [
                idKey,
                hydrate(rowWorkerIds),
              ])
            );
            setWorkScopeAspects((prev) =>
              prev.map((row) => {
                const nextAssignment = nextAssignmentsByAspect.get(String(row.id));
                return nextAssignment
                  ? { ...row, worker_assignment: nextAssignment }
                  : row;
              })
            );
          }
        }
        setAspectWorkersModal(null);
      };

      const runApplyWorkersSafely = async (summariesForResolution = []) => {
        try {
          await runApplyWorkers(summariesForResolution);
        } catch (err) {
          console.error("Schedule apply workers to all days failed", err);
        }
      };
      if (conflictSummaries.length > 0) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries,
          onConfirm: () => {
            void runApplyWorkersSafely(conflictSummaries);
          },
        });
      } else {
        await runApplyWorkersSafely();
      }
    },
    [
      aspectWorkersModal,
      blocksRef,
      requestWorkerConflictConfirmation,
      runPlanningOperation,
      setBlocks,
      setWorkScopeAspects,
      workers,
      workScopeAspectsRef,
    ]
  );

  /** Route crew modal submit to draft, block-level, or per-day assignment updates. */
  const submitAspectWorkersModal = useCallback(
    async (workerIds) => {
      const modal = aspectWorkersModal;
      if (!modal) {
        return;
      }
      if (modal.aspectId === "block-draft") {
        const catalog = workers ?? [];
        const byId = buildMapByNumericId(catalog);
        const nextAssignment = pickItemsByNumericIdsInOrder(byId, workerIds);
        blockDraftWorkers.current = nextAssignment;
        setAspectWorkersModal(null);
        return;
      }
      if (modal.crewEditMode === "block") {
        await applyAspectWorkersToAllDays(workerIds);
        return;
      }
      const { aspectId } = modal;
      const aspect = workScopeAspects.find((row) => String(row.id) === String(aspectId));
      const block = modal.block;
      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);
      const proposedAspect = aspect
        ? {
          ...aspect,
          worker_assignment: pickItemsByNumericIdsInOrder(byId, workerIds),
        }
        : null;
      const dayKey = proposedAspect?.date ? toLocalDateKey(proposedAspect.date) : null;
      const dayConflict =
        proposedAspect && dayKey
          ? getWorkerConflictSummariesByDate(
            workScopeAspects.map((row) =>
              String(row.id) === String(aspectId) ? proposedAspect : row
            ),
            [dayKey],
            { workers }
          ).find((summary) => summary.conflictWorkerIds.length > 0)
          : null;

      const runUpdateDayCrew = async (conflictSummary = null) => {
        const filteredWorkerIds =
          conflictSummary && block
            ? buildFilteredWorkerIdsAfterConflictStrip({
              block,
              aspect: proposedAspect,
              conflictWorkerIds: conflictSummary.conflictWorkerIds,
            })
            : workerIds;
        await runPlanningOperation({
          module: "schedule",
          operation: "updateScheduleDayCrew",
          resources: ["schedule.aspects"],
          apiCall: () => patchScopeAspects(aspectId, filteredWorkerIds, "schedule"),
        });
        const nextAssignment = pickItemsByNumericIdsInOrder(byId, filteredWorkerIds);
        const idKey = String(aspectId);
        setWorkScopeAspects((prev) =>
          prev.map((row) =>
            String(row.id) === idKey
              ? { ...row, worker_assignment: nextAssignment }
              : row
          )
        );
        setAspectWorkersModal(null);
      };
      const runUpdateDayCrewSafely = async (conflictSummary = null) => {
        try {
          await runUpdateDayCrew(conflictSummary);
        } catch (err) {
          console.error("Schedule aspect workers update failed", err);
        }
      };
      if (dayConflict) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries: [dayConflict],
          onConfirm: () => {
            void runUpdateDayCrewSafely(dayConflict);
          },
        });
        return;
      }
      await runUpdateDayCrewSafely();
    },
    [
      applyAspectWorkersToAllDays,
      aspectWorkersModal,
      blockDraftWorkers,
      requestWorkerConflictConfirmation,
      runPlanningOperation,
      workers,
      setWorkScopeAspects,
      workScopeAspects,
    ]
  );

  /**
   * Resize the orange crew-override strip: days leaving the strip get block default crew;
   * days inside the new strip get `overrideWorkerIds`.
   */
  const handleCommitOverrideStripRange = useCallback(
    async ({
      blockId,
      previousStartDay,
      previousEndDay,
      nextStartDay,
      nextEndDay,
      overrideWorkerIds,
    }) => {
      const touchesLockedDay = overrideRangeResizeChangedDaysTouchLockedDay({
        previousStartDay,
        previousEndDay,
        nextStartDay,
        nextEndDay,
        dayLocks: scheduleDayLocks,
      });
      if (touchesLockedDay) {
        notify?.(
          PLANNING_OPERATION_MESSAGES.scheduleCannotChangeCrewOverrideDueToLockedDays,
          "error"
        );
        return;
      }
      const block = blocks.find((b) => sameScheduleBlockId(b.id, blockId));
      if (!block) {
        return;
      }
      const defaultIds = (block.worker_assignment ?? []).map((w) => w.id);
      const targetIds =
        Array.isArray(overrideWorkerIds) && overrideWorkerIds.length > 0
          ? overrideWorkerIds
          : defaultIds;

      const enumerateKeys = (start, end) => {
        const a = toLocalCalendarDay(start);
        const b = toLocalCalendarDay(end);
        const keys = [];
        let d = new Date(a);
        while (toLocalDateKey(d) <= toLocalDateKey(b)) {
          keys.push(toLocalDateKey(d));
          d = getShiftedDate(d, 1);
        }
        return keys;
      };

      const prevKeys = new Set(enumerateKeys(previousStartDay, previousEndDay));
      const nextKeys = new Set(enumerateKeys(nextStartDay, nextEndDay));
      const clearKeys = [...prevKeys].filter((k) => !nextKeys.has(k));
      const applyKeys = [...nextKeys].filter((k) => !prevKeys.has(k));

      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);

      const patchRows = [];
      const collectPatchForDayKey = (dayKey, workerIds) => {
        const aspect = workScopeAspects.find(
          (a) =>
            sameScheduleBlockId(getAspectBlockId(a), blockId) &&
            toLocalDateKey(a.date) === dayKey
        );
        if (!aspect || !/^\d+$/.test(String(aspect.id))) {
          return;
        }
        patchRows.push({
          scopeId: Number(aspect.id),
          workerIds,
          idKey: String(aspect.id),
          dayKey,
          aspect,
        });
      };

      for (const k of clearKeys) {
        collectPatchForDayKey(k, defaultIds);
      }
      for (const k of applyKeys) {
        collectPatchForDayKey(k, targetIds);
      }

      if (patchRows.length === 0) {
        return;
      }

      const desiredByAspectId = new Map(
        patchRows.map(({ idKey, workerIds, aspect }) => [
          idKey,
          {
            ...aspect,
            worker_assignment: pickItemsByNumericIdsInOrder(byId, workerIds),
          },
        ])
      );
      const mergedAspectsForCheck = workScopeAspects.map((row) => {
        const desired = desiredByAspectId.get(String(row.id));
        return desired ?? row;
      });
      const dateKeysToCheck = [...new Set(patchRows.map((row) => row.dayKey))];
      const conflictSummaries = getWorkerConflictSummariesByDate(
        mergedAspectsForCheck,
        dateKeysToCheck,
        { workers }
      ).filter((summary) => summary.conflictWorkerIds.length > 0);

      const runOverridePatch = async (summariesForResolution = []) => {
        const summaryByDateKey = new Map(
          summariesForResolution.map((summary) => [summary.dateKey, summary])
        );
        const finalPatchRows = patchRows.map((row) => {
          const summary = summaryByDateKey.get(row.dayKey);
          if (!summary || summary.conflictWorkerIds.length === 0) {
            return row;
          }
          const desiredAspect = {
            ...row.aspect,
            worker_assignment: pickItemsByNumericIdsInOrder(byId, row.workerIds),
          };
          const filteredIds = buildFilteredWorkerIdsAfterConflictStrip({
            block,
            aspect: desiredAspect,
            conflictWorkerIds: summary.conflictWorkerIds,
          });
          return {
            ...row,
            workerIds: filteredIds,
          };
        });

        await runPlanningOperation({
          module: "schedule",
          operation: "updateScheduleOverrideRange",
          resources: ["schedule.aspects"],
          apiCall: () =>
            patchScopeAspects(
              finalPatchRows.map(({ scopeId, workerIds }) => ({ scopeId, workerIds })),
              undefined,
              "schedule"
            ),
        });

        const nextAssignmentsByAspect = new Map(
          finalPatchRows.map(({ idKey, workerIds }) => [
            idKey,
            pickItemsByNumericIdsInOrder(byId, workerIds),
          ])
        );
        setWorkScopeAspects((prev) =>
          prev.map((row) => {
            const nextAssignment = nextAssignmentsByAspect.get(String(row.id));
            return nextAssignment
              ? { ...row, worker_assignment: nextAssignment }
              : row;
          })
        );
      };

      const runOverridePatchSafely = async (summariesForResolution = []) => {
        try {
          await runOverridePatch(summariesForResolution);
        } catch (err) {
          console.error("Schedule override strip range patch failed", err);
        }
      };

      if (conflictSummaries.length > 0) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries,
          onConfirm: () => {
            void runOverridePatchSafely(conflictSummaries);
          },
        });
        return;
      }
      await runOverridePatchSafely();

    },
    [
      blocks,
      notify,
      requestWorkerConflictConfirmation,
      runPlanningOperation,
      scheduleDayLocks,
      setWorkScopeAspects,
      workScopeAspects,
      workers,
    ]
  );

  /** Clear a single-day crew override by restoring the block's default worker set. */
  const handleRemoveDayCrewOverride = useCallback(
    async (blockId, day) => {
      const dayKey = toLocalDateKey(day);
      const block = blocks.find((b) => sameScheduleBlockId(b.id, blockId));
      const aspect = workScopeAspects.find(
        (a) =>
          sameScheduleBlockId(getAspectBlockId(a), blockId) &&
          toLocalDateKey(a.date) === dayKey
      );
      if (!block || !aspect || !/^\d+$/.test(String(aspect.id))) {
        return;
      }
      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);
      const workerIds = (block.worker_assignment ?? []).map((w) => w.id);
      const proposedAspect = {
        ...aspect,
        worker_assignment: pickItemsByNumericIdsInOrder(byId, workerIds),
      };
      const mergedAspectsForCheck = workScopeAspects.map((row) =>
        String(row.id) === String(aspect.id) ? proposedAspect : row
      );
      const dayConflict = getWorkerConflictSummariesByDate(
        mergedAspectsForCheck,
        [dayKey],
        { workers }
      ).find((summary) => summary.conflictWorkerIds.length > 0);

      const runRemoveDayOverride = async (conflictSummary = null) => {
        const filteredWorkerIds = conflictSummary
          ? buildFilteredWorkerIdsAfterConflictStrip({
              block,
              aspect: proposedAspect,
              conflictWorkerIds: conflictSummary.conflictWorkerIds,
            })
          : workerIds;
        await runPlanningOperation({
          module: "schedule",
          operation: "removeScheduleDayCrewOverride",
          resources: ["schedule.aspects"],
          apiCall: () => patchScopeAspects(aspect.id, filteredWorkerIds, "schedule"),
        });
        const nextAssignment = pickItemsByNumericIdsInOrder(byId, filteredWorkerIds);
        const idKey = String(aspect.id);
        setWorkScopeAspects((prev) =>
          prev.map((row) =>
            String(row.id) === idKey
              ? { ...row, worker_assignment: nextAssignment }
              : row
          )
        );
      };

      const runRemoveDayOverrideSafely = async (conflictSummary = null) => {
        try {
          await runRemoveDayOverride(conflictSummary);
        } catch (err) {
          console.error("Schedule remove day crew override failed", err);
        }
      };
      if (dayConflict) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries: [dayConflict],
          onConfirm: () => {
            void runRemoveDayOverrideSafely(dayConflict);
          },
        });
      } else {
        await runRemoveDayOverrideSafely();
      }
    },
    [
      blocks,
      requestWorkerConflictConfirmation,
      runPlanningOperation,
      setWorkScopeAspects,
      workScopeAspects,
      workers,
    ]
  );

  /** Copy a worker id list from CrewCard actions into schedule clipboard state. */
  const copyScheduleCrew = useCallback((workerIds) => {
    const ids = Array.isArray(workerIds)
      ? workerIds.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      : [];
    setScheduleCrewClipboard({ workerIds: ids });
  }, []);

  /** Paste copied crew ids to a whole block or a single day aspect. */
  const pasteScheduleCrew = useCallback(
    async ({ mode, blockId, day }) => {
      if (!scheduleCrewClipboard || !Array.isArray(scheduleCrewClipboard.workerIds)) {
        return;
      }
      const workerIds = scheduleCrewClipboard.workerIds;
      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);
      const hydrate = (ids) => pickItemsByNumericIdsInOrder(byId, ids);

      if (mode === "block") {
        if (!/^\d+$/.test(String(blockId))) {
          return;
        }
        const targetBlock = blocksRef.current.find((b) =>
          sameScheduleBlockId(b.id, blockId)
        );
        if (!targetBlock) {
          return;
        }
        const oldDefaultIdsSorted = (targetBlock.worker_assignment ?? [])
          .map((worker) => Number(worker?.id))
          .filter((id) => Number.isFinite(id))
          .sort((a, b) => a - b);
        const changedDateKeys = [];
        const mergedAspectsForCheck = workScopeAspectsRef.current.map((row) => {
          if (!sameScheduleBlockId(getAspectBlockId(row), blockId)) {
            return row;
          }
          const rowIdsSorted = (row.worker_assignment ?? [])
            .map((worker) => Number(worker?.id))
            .filter((id) => Number.isFinite(id))
            .sort((a, b) => a - b);
          const rowMatchesOldDefault =
            rowIdsSorted.length === oldDefaultIdsSorted.length &&
            rowIdsSorted.every((id, index) => id === oldDefaultIdsSorted[index]);
          if (!rowMatchesOldDefault) {
            return row;
          }
          changedDateKeys.push(toLocalDateKey(row.date));
          return { ...row, worker_assignment: hydrate(workerIds) };
        });
        const conflictSummaries = getWorkerConflictSummariesByDate(
          mergedAspectsForCheck,
          [...new Set(changedDateKeys)],
          { workers }
        ).filter((summary) => summary.conflictWorkerIds.length > 0);

        const runPasteBlockCrew = async (summariesForResolution = []) => {
          const raw = await runPlanningOperation({
            module: "schedule",
            operation: "updateScheduleBlockDefaultCrew",
            resources: ["schedule.blocks", "schedule.aspects"],
            apiCall: () => postScheduleBlockApplyWorkers(blockId, workerIds),
          });
          const mapped = mapBlockFromApi(raw);
          const mappedAspects = (raw.aspects ?? []).map((a) =>
            mapAspectFromApi(a, mapped.id)
          );
          setBlocks((prev) =>
            prev.map((b) => (sameScheduleBlockId(b.id, blockId) ? mapped : b))
          );
          setWorkScopeAspects((prev) => [
            ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
            ...mappedAspects,
          ]);
          if (summariesForResolution.length === 0) {
            return;
          }
          const summaryByDateKey = new Map(
            summariesForResolution.map((summary) => [summary.dateKey, summary])
          );
          const conflictPatchRows = [];
          for (const aspect of mappedAspects) {
            if (!/^\d+$/.test(String(aspect.id))) {
              continue;
            }
            const summary = summaryByDateKey.get(toLocalDateKey(aspect.date));
            if (!summary || summary.conflictWorkerIds.length === 0) {
              continue;
            }
            const filteredWorkerIds = buildFilteredWorkerIdsAfterConflictStrip({
              block: mapped,
              aspect,
              conflictWorkerIds: summary.conflictWorkerIds,
            });
            conflictPatchRows.push({
              scopeId: Number(aspect.id),
              workerIds: filteredWorkerIds,
              idKey: String(aspect.id),
            });
          }
          if (conflictPatchRows.length === 0) {
            return;
          }
          await runPlanningOperation({
            module: "schedule",
            operation: "updateScheduleConflictCrewOverrides",
            resources: ["schedule.aspects"],
            apiCall: () =>
              patchScopeAspects(
                conflictPatchRows.map(({ scopeId, workerIds: rowWorkerIds }) => ({
                  scopeId,
                  workerIds: rowWorkerIds,
                })),
                undefined,
                "schedule"
              ),
          });
          const nextAssignmentsByAspect = new Map(
            conflictPatchRows.map(({ idKey, workerIds: rowWorkerIds }) => [
              idKey,
              hydrate(rowWorkerIds),
            ])
          );
          setWorkScopeAspects((prev) =>
            prev.map((row) => {
              const nextAssignment = nextAssignmentsByAspect.get(String(row.id));
              return nextAssignment
                ? { ...row, worker_assignment: nextAssignment }
                : row;
            })
          );
        };

        const runPasteBlockCrewSafely = async (summariesForResolution = []) => {
          try {
            await runPasteBlockCrew(summariesForResolution);
          } catch (err) {
            console.error("Schedule paste crew to block failed", err);
          }
        };
        if (conflictSummaries.length > 0) {
          requestWorkerConflictConfirmation?.({
            conflictSummaries,
            onConfirm: () => {
              void runPasteBlockCrewSafely(conflictSummaries);
            },
          });
        } else {
          await runPasteBlockCrewSafely();
        }
        return;
      }

      const dayKey = toLocalDateKey(day);
      const aspect = workScopeAspectsRef.current.find(
        (a) =>
          sameScheduleBlockId(getAspectBlockId(a), blockId) &&
          toLocalDateKey(a.date) === dayKey
      );
      if (!aspect || !/^\d+$/.test(String(aspect.id))) {
        return;
      }
      const block =
        blocksRef.current.find((b) => sameScheduleBlockId(b.id, blockId)) ?? null;
      if (!block) {
        return;
      }
      const proposedAspect = {
        ...aspect,
        worker_assignment: hydrate(workerIds),
      };
      const mergedAspectsForCheck = workScopeAspectsRef.current.map((row) =>
        String(row.id) === String(aspect.id) ? proposedAspect : row
      );
      const dayConflict = getWorkerConflictSummariesByDate(
        mergedAspectsForCheck,
        [dayKey],
        { workers }
      ).find((summary) => summary.conflictWorkerIds.length > 0);

      const runPasteDayCrew = async (conflictSummary = null) => {
        const filteredWorkerIds = conflictSummary
          ? buildFilteredWorkerIdsAfterConflictStrip({
              block,
              aspect: proposedAspect,
              conflictWorkerIds: conflictSummary.conflictWorkerIds,
            })
          : workerIds;
        await runPlanningOperation({
          module: "schedule",
          operation: "updateScheduleDayCrew",
          resources: ["schedule.aspects"],
          apiCall: () => patchScopeAspects(aspect.id, filteredWorkerIds, "schedule"),
        });
        const nextAssignment = hydrate(filteredWorkerIds);
        const idKey = String(aspect.id);
        setWorkScopeAspects((prev) =>
          prev.map((row) =>
            String(row.id) === idKey
              ? { ...row, worker_assignment: nextAssignment }
              : row
          )
        );
      };

      const runPasteDayCrewSafely = async (conflictSummary = null) => {
        try {
          await runPasteDayCrew(conflictSummary);
        } catch (err) {
          console.error("Schedule paste crew to day failed", err);
        }
      };
      if (dayConflict) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries: [dayConflict],
          onConfirm: () => {
            void runPasteDayCrewSafely(dayConflict);
          },
        });
      } else {
        await runPasteDayCrewSafely();
      }
    },
    [
      blocksRef,
      runPlanningOperation,
      requestWorkerConflictConfirmation,
      scheduleCrewClipboard,
      setBlocks,
      setWorkScopeAspects,
      workers,
      workScopeAspectsRef,
    ]
  );

  /**
   * Confirm unskipping a day that would double-book workers, then persist the unskip
   * and strip conflicting workers from that day's per-day crew override.
   */
  const confirmWorkerUnskipConflictModal = useCallback(async () => {
    const pending = workerUnskipConflictModal;
    if (!pending) {
      return;
    }
    const { aspect, block, conflictSummary } = pending;
    const idKey = String(aspect.id);
    const workerIds = buildFilteredWorkerIdsAfterConflictStrip({
      block,
      aspect,
      conflictWorkerIds: conflictSummary.conflictWorkerIds,
    });
    try {
      await runPlanningOperation({
        module: "schedule",
        operation: "removeScopeSkipped",
        resources: ["schedule.aspects"],
        apiCall: () => updateScope(Number(aspect.id), { skipped: false }, "schedule"),
      });
      await runPlanningOperation({
        module: "schedule",
        operation: "removeScopeSkippedUnavailableWorkers",
        resources: ["schedule.aspects"],
        apiCall: () => patchScopeAspects(Number(aspect.id), workerIds, "schedule"),
      });
      const catalog = workers ?? [];
      const byId = buildMapByNumericId(catalog);
      const nextAssignment = pickItemsByNumericIdsInOrder(byId, workerIds);
      setWorkScopeAspects((prev) =>
        prev.map((row) =>
          String(row.id) === idKey
            ? { ...row, skipped: false, worker_assignment: nextAssignment }
            : row
        )
      );
      setWorkerUnskipConflictModal(null);
    } catch (err) {
      console.error("Schedule unskip conflict override failed", err);
      setWorkScopeAspects((prev) =>
        prev.map((row) =>
          String(row.id) === idKey ? { ...row, skipped: true } : row
        )
      );
      setWorkerUnskipConflictModal(null);
    }
  }, [
    runPlanningOperation,
    setWorkScopeAspects,
    workerUnskipConflictModal,
    workers,
  ]);

  /** Cancel an unskip that would conflict, restoring the skipped state locally. */
  const cancelWorkerUnskipConflictModal = useCallback(() => {
    const pending = workerUnskipConflictModal;
    if (pending) {
      const idKey = String(pending.aspect.id);
      setWorkScopeAspects((prev) =>
        prev.map((row) =>
          String(row.id) === idKey ? { ...row, skipped: true } : row
        )
      );
    }
    setWorkerUnskipConflictModal(null);
  }, [setWorkScopeAspects, workerUnskipConflictModal]);

  /** Optimistically merge a skipped-day reason into the matching local aspect row. */
  const handleSkippedReasonChange = useCallback(
    (aspectId, skippedReason) => {
      const idKey = String(aspectId);
      setWorkScopeAspects((prev) =>
        prev.map((row) =>
          String(row.id) === idKey
            ? { ...row, skipped_reason: skippedReason }
            : row
        )
      );
    },
    [setWorkScopeAspects]
  );

  /** ⌘/Ctrl + short click on a bar day: flip `skipped` on that aspect (PATCH dispatch edit). */
  const handleToggleAspectSkipped = useCallback(
    (blockId, day) => {
      if (blockId == null || !(day instanceof Date)) {
        return;
      }
      const dayCal = toLocalCalendarDay(day);
      if (dateRangeHasLockedDay(dayCal, dayCal, scheduleDayLocks)) {
        notify?.(
          PLANNING_OPERATION_MESSAGES.scheduleUnableToSetSkippedOnLockedDays,
          "error"
        );
        return;
      }
      const dayKey = toLocalDateKey(day);
      const aspect = workScopeAspectsRef.current.find(
        (a) =>
          sameScheduleBlockId(getAspectBlockId(a), blockId) &&
          toLocalDateKey(a.date) === dayKey
      );
      if (!aspect || !/^\d+$/.test(String(aspect.id))) {
        return;
      }
      const idKey = String(aspect.id);
      const nextSkipped = !Boolean(aspect.skipped);
      const prevSkipped = Boolean(aspect.skipped);
      const block =
        blocksRef.current.find((b) => sameScheduleBlockId(b.id, blockId)) ?? null;

      setWorkScopeAspects((prev) =>
        prev.map((row) =>
          String(row.id) === idKey ? { ...row, skipped: nextSkipped } : row
        )
      );
      if (!nextSkipped && block) {
        const mergedAspects = workScopeAspectsRef.current.map((row) =>
          String(row.id) === idKey ? { ...row, skipped: false } : row
        );
        const conflictSummary = getWorkerConflictSummariesByDate(
          mergedAspects,
          [dayKey],
          { workers }
        ).find((summary) => summary.conflictWorkerIds.length > 0);

        if (conflictSummary) {
          setWorkerUnskipConflictModal({
            conflictSummary,
            summaries: [conflictSummary],
            aspect: { ...aspect, skipped: false },
            block,
            day,
          });
          return;
        }
      }

      runPlanningOperation({
        module: "schedule",
        operation: nextSkipped ? "setScopeSkipped" : "removeScopeSkipped",
        resources: ["schedule.aspects"],
        apiCall: () => updateScope(Number(aspect.id), { skipped: nextSkipped }, "schedule"),
      }).catch((err) => {
        console.error("Schedule toggle aspect skipped failed", err);
        setWorkScopeAspects((prev) =>
          prev.map((row) =>
            String(row.id) === idKey ? { ...row, skipped: prevSkipped } : row
          )
        );
      });
    },
    [
      blocksRef,
      notify,
      runPlanningOperation,
      scheduleDayLocks,
      setWorkScopeAspects,
      workers,
      workScopeAspectsRef,
    ]
  );

  return {
    aspectWorkersModal,
    scheduleWorkerModalUsedWorkers,
    scheduleCrewClipboard,
    workerUnskipConflictModal: workerUnskipConflictModal?.summaries ?? null,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    handleSkippedReasonChange,
    closeAspectWorkersModal,
    submitAspectWorkersModal,
    applyAspectWorkersToAllDays,
    handleBarDayShortClick,
    handleOpenScopeCrewModal,
    handleRemoveDayCrewOverride,
    handleCommitOverrideStripRange,
    handleToggleAspectSkipped,
    copyScheduleCrew,
    pasteScheduleCrew,
  };
}

